import { useState, useEffect, useCallback } from 'react'
import type { FC, KeyboardEvent } from 'react'

interface RhymeSearchInputProps {
  /** Current search term value */
  value: string
  /** Called when input changes (after debounce delay) */
  onChange: (value: string) => void
  /** Called when user presses Enter to submit search */
  onSubmit?: () => void
  /** Called when user clicks clear button */
  onClear: () => void
  /** Placeholder text for input */
  placeholder?: string
}

/**
 * Debounced search input for rhyme exploration.
 *
 * Features:
 * - 1000ms debounce on onChange to prevent excessive calls
 * - Enter key triggers immediate onSubmit (bypasses debounce)
 * - Clear button appears when input has value
 * - Preserves existing test IDs for compatibility
 *
 * Note: This component manages internal debounce state while controlled
 * by parent via value prop. onChange fires after debounce completes.
 */
export const RhymeSearchInput: FC<RhymeSearchInputProps> = ({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder = 'Search word...'
}) => {
  // Local state for immediate display (before debounce)
  const [localValue, setLocalValue] = useState(value)

  // Sync local value when controlled value changes externally
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  // Debounced onChange handler
  useEffect(() => {
    // If local matches controlled, no debounce needed
    if (localValue === value) return

    const timer = setTimeout(() => {
      onChange(localValue)
    }, 1000)

    return () => clearTimeout(timer)
  }, [localValue, value, onChange])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSubmit) {
      e.preventDefault()
      // Flush any pending debounce immediately
      if (localValue !== value) {
        onChange(localValue)
      }
      onSubmit()
    }
  }, [localValue, value, onChange, onSubmit])

  const handleClear = useCallback(() => {
    setLocalValue('')
    onClear()
  }, [onClear])

  return (
    <div className="relative">
      <input
        type="text"
        data-testid="worksheet-search-input"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full px-3 py-2 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {localValue && (
        <button
          data-testid="worksheet-search-clear"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          aria-label="Clear search"
        >
          ✕
        </button>
      )}
    </div>
  )
}
